import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { UserService } from '../../shared/core/state/users/users.service';
import {
  InvitationService,
  InvitationItem,
  InvitationStatus,
} from '../../shared/services/invitation.service';
import {
  UserCompatible,
  UserStatus,
  isEmail,
} from '../../shared/core/state/users/models';
import { ToastService } from '@black/ui';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SubscriptionService } from '../../shared/core/state/subscription/subscription.service';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { CourseService } from '../../shared/core/state/course/course.service';
import { BatchService, BatchItem } from '../../shared/services/batch.service';

export interface StudentSummary {
  id: string;
  studentId?: string;
  username: string;
  role: string;
  status: string;
  isManuallyAdded?: boolean;
  addedBy?: any;
  name: {
    firstName?: string;
    lastName?: string;
  };
  profile?: any;
  tenant?: any;
  createdAt?: string;
  subscriptions?: any[];
  enrollments?: any[];
}

const INITIAL_MANUAL_STUDENT_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  studentId: '',
  liveClassId: '',
  courseId: '',
  notes: '',
};

@Component({
  selector: 'black-student-management',
  templateUrl: './student-management.component.html',
  styleUrls: ['./student-management.component.css'],
  standalone: false,
})
export class StudentManagementComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private invitationService = inject(InvitationService);
  private toast = inject(ToastService);
  private http = inject(HttpClient);
  private subscriptionService = inject(SubscriptionService);
  private liveClassService = inject(LiveClassService);
  private courseService = inject(CourseService);
  private batchService = inject(BatchService);

  readonly Math = Math;

  // Tabs
  activeTab: 'STUDENTS' | 'INVITATIONS' = 'STUDENTS';

  // Batches
  batches: BatchItem[] = [];
  selectedBatchFilter = '';

  // Learning Item Assignment & Subscription management
  showAssignModal = false;
  assignType: 'LIVE_CLASS' | 'COURSE' = 'LIVE_CLASS';
  availableClasses: any[] = [];
  availableCourses: any[] = [];
  loadingLearningItems = false;
  selectedClassId = '';
  selectedCourseId = '';
  selectedPaymentType = 'MONTHLY';
  selectedCourseDurationDays = 365;
  submittingAssign = signal(false);
  modifyingSubscription = signal<string | null>(null);

  // Students state
  students: UserCompatible[] = [];
  totalStudents = 0;
  verifiedStudentsCount = 0;
  blockedStudentsCount = 0;
  studentPage = 1;
  studentLimit = 10;
  loadingStudents = false;
  studentSearch = '';
  studentStatusFilter = '';

  // Student Details Drawer
  selectedStudent: UserCompatible | null = null;
  studentSummary: StudentSummary | null = null;
  loadingSummary = false;
  showDetailsDrawer = false;

  // Invitations state
  invitations: InvitationItem[] = [];
  totalInvitations = 0;
  pendingInvitationsCount = 0;
  invitationPage = 1;
  invitationLimit = 10;
  loadingInvitations = false;
  invitationSearch = '';
  invitationStatusFilter = '';

  // Invite Modal
  showInviteModal = false;

  // Manual Student Onboarding Modal
  showAddManualStudentModal = false;
  submittingManualStudent = signal(false);
  manualStudentForm = { ...INITIAL_MANUAL_STUDENT_FORM };

  // Admin Reset Password Modal
  showResetPasswordModal = false;
  resetPasswordStudent: UserCompatible | null = null;
  newPassword = '';
  submittingResetPassword = signal(false);

  // Action processing states
  processingAction = signal(false);

  readonly UserStatus = UserStatus;
  readonly InvitationStatus = InvitationStatus;

  private destroy$ = new Subject<void>();
  private studentSearch$ = new Subject<string>();
  private studentSub?: Subscription;
  private invitationSearch$ = new Subject<string>();
  private invitationSub?: Subscription;

  ngOnInit(): void {
    this.studentSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.studentPage = 1;
        this.loadStudents();
      });

    this.invitationSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.invitationPage = 1;
        this.loadInvitations();
      });

    this.loadStudents();
    this.loadInvitations();
    this.loadLearningItems();
    this.loadBatches();
  }

  loadBatches(): void {
    this.batchService
      .getBatches(1, 100, undefined, 'ACTIVE')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.batches = Array.isArray(res) ? res : res?.docs ?? [];
        },
        error: () => {
          this.batches = [];
        },
      });
  }

  onBatchFilterChange(): void {
    this.studentPage = 1;
    this.loadStudents();
  }

  loadLearningItems(): void {
    this.loadingLearningItems = true;
    this.liveClassService
      .getAll(1, 100, undefined, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingLearningItems = false)),
      )
      .subscribe({
        next: (res: any) => {
          this.availableClasses = res.docs ?? [];
          if (!this.selectedClassId && this.availableClasses.length > 0) {
            this.selectedClassId =
              this.availableClasses[0]._id || this.availableClasses[0].id;
          }
        },
        error: () => {
          this.availableClasses = [];
        },
      });

    this.courseService
      .getAll(1, 100, undefined, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.availableCourses = res.docs ?? [];
          if (!this.selectedCourseId && this.availableCourses.length > 0) {
            this.selectedCourseId = this.availableCourses[0]._id;
          }
        },
        error: () => {
          this.availableCourses = [];
        },
      });
  }

  ngOnDestroy(): void {
    this.studentSub?.unsubscribe();
    this.invitationSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTab(tab: 'STUDENTS' | 'INVITATIONS'): void {
    this.activeTab = tab;
    if (tab === 'STUDENTS') {
      this.loadStudents();
    } else {
      this.loadInvitations();
    }
  }

  loadStudents(): void {
    this.studentSub?.unsubscribe();
    this.loadingStudents = true;
    this.studentSub = this.userService
      .getAll(this.studentPage, this.studentLimit, {
        search: this.studentSearch?.trim() || undefined,
        role: 'STUDENT',
        status: this.studentStatusFilter || undefined,
        batchId: this.selectedBatchFilter || undefined,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingStudents = false)),
      )
      .subscribe({
        next: (res: any) => {
          this.students = (res.docs ?? []) as UserCompatible[];
          this.totalStudents = res.totalDocs ?? 0;
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to load students.',
            'error',
          );
        },
      });
  }

  loadInvitations(): void {
    this.invitationSub?.unsubscribe();
    this.loadingInvitations = true;
    this.invitationSub = this.invitationService
      .getInvitations({
        page: this.invitationPage,
        limit: this.invitationLimit,
        search: this.invitationSearch?.trim() || undefined,
        status: this.invitationStatusFilter || undefined,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingInvitations = false)),
      )
      .subscribe({
        next: (res) => {
          this.invitations = res.docs ?? [];
          this.totalInvitations = res.totalDocs ?? 0;
          this.pendingInvitationsCount = this.invitations.filter(
            (i) => i.status === InvitationStatus.PENDING,
          ).length;
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to load invitations.',
            'error',
          );
        },
      });
  }

  onStudentSearchChange(query: string): void {
    this.studentSearch = query;
    this.studentSearch$.next(query);
  }

  clearStudentSearch(): void {
    this.studentSearch = '';
    this.studentPage = 1;
    this.loadStudents();
  }

  onStudentFilterChange(): void {
    this.studentPage = 1;
    this.loadStudents();
  }

  onStudentPageChange(page: number): void {
    this.studentPage = page;
    this.loadStudents();
  }

  onInvitationSearchChange(query: string): void {
    this.invitationSearch = query;
    this.invitationSearch$.next(query);
  }

  clearInvitationSearch(): void {
    this.invitationSearch = '';
    this.invitationPage = 1;
    this.loadInvitations();
  }

  onInvitationFilterChange(): void {
    this.invitationPage = 1;
    this.loadInvitations();
  }

  onInvitationPageChange(page: number): void {
    this.invitationPage = page;
    this.loadInvitations();
  }

  openStudentDetails(student: UserCompatible): void {
    this.selectedStudent = student;
    this.showDetailsDrawer = true;
    this.loadStudentSummary(student._id);
  }

  loadStudentSummary(studentId: string): void {
    this.loadingSummary = true;
    this.http
      .get<StudentSummary>(
        `${environment.host}/api/v1/user/students/${studentId}/summary`,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingSummary = false)),
      )
      .subscribe({
        next: (summary) => {
          this.studentSummary = summary;
        },
        error: () => {
          if (!this.studentSummary && this.selectedStudent) {
            const s = this.selectedStudent;
            this.studentSummary = {
              id: s._id,
              username: s.username,
              role: s.role || 'STUDENT',
              status: s.status,
              name: {
                firstName: s.profile?.personal?.firstName,
                lastName: s.profile?.personal?.lastName,
              },
              profile: s.profile,
              subscriptions: [],
            };
          }
        },
      });
  }

  closeStudentDetails(): void {
    this.showDetailsDrawer = false;
    this.selectedStudent = null;
    this.studentSummary = null;
  }

  private mutateSubscription(
    subId: string,
    action$: Observable<unknown>,
    successMsg: string,
  ): void {
    this.modifyingSubscription.set(subId);
    action$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.modifyingSubscription.set(null)),
      )
      .subscribe({
        next: () => {
          this.toast.showToast(successMsg, 'success');
          if (this.selectedStudent) {
            this.loadStudentSummary(this.selectedStudent._id);
          }
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Operation failed.',
            'error',
          );
        },
      });
  }

  pauseSubscription(sub: any): void {
    if (!sub?._id) return;
    this.mutateSubscription(
      sub._id,
      this.subscriptionService.suspendSubscription(sub._id),
      'Subscription paused successfully.',
    );
  }

  resumeSubscription(sub: any): void {
    if (!sub?._id) return;
    this.mutateSubscription(
      sub._id,
      this.subscriptionService.activateSubscription(sub._id),
      'Subscription resumed successfully.',
    );
  }

  cancelStudentSubscription(sub: any): void {
    if (!sub?._id) return;
    this.mutateSubscription(
      sub._id,
      this.subscriptionService.forceCancelSubscription(sub._id),
      'Subscription cancelled.',
    );
  }

  openAssignModal(type: 'LIVE_CLASS' | 'COURSE' = 'LIVE_CLASS'): void {
    this.showAssignModal = true;
    this.assignType = type;
    this.selectedPaymentType = 'MONTHLY';
    this.selectedCourseDurationDays = 365;

    if (this.availableClasses.length > 0) {
      this.selectedClassId =
        this.availableClasses[0]._id || this.availableClasses[0].id;
    }
    if (this.availableCourses.length > 0) {
      this.selectedCourseId = this.availableCourses[0]._id;
    }
    this.loadLearningItems();
  }

  closeAssignModal(): void {
    this.showAssignModal = false;
  }

  submitAssignSubscription(): void {
    if (!this.selectedStudent?._id) return;

    if (this.assignType === 'LIVE_CLASS') {
      if (!this.selectedClassId) {
        this.toast.showToast('Please select a class to assign.', 'error');
        return;
      }

      this.submittingAssign.set(true);
      this.subscriptionService
        .assignSubscription({
          liveClass: this.selectedClassId,
          user: this.selectedStudent._id,
          paymentType: this.selectedPaymentType,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.submittingAssign.set(false)),
        )
        .subscribe({
          next: () => {
            this.toast.showToast(
              'Subscription assigned successfully!',
              'success',
            );
            this.closeAssignModal();
            if (this.selectedStudent) {
              this.loadStudentSummary(this.selectedStudent._id);
            }
          },
          error: (err) => {
            this.toast.showToast(
              err?.error?.message ?? 'Failed to assign subscription.',
              'error',
            );
          },
        });
    } else {
      if (!this.selectedCourseId) {
        this.toast.showToast('Please select a course to assign.', 'error');
        return;
      }

      this.submittingAssign.set(true);
      this.courseService
        .enrollStudent(this.selectedCourseId, {
          studentId: this.selectedStudent._id,
          expiresInDays: Number(this.selectedCourseDurationDays) || 365,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.submittingAssign.set(false)),
        )
        .subscribe({
          next: () => {
            this.toast.showToast(
              'Course enrollment assigned successfully!',
              'success',
            );
            this.closeAssignModal();
            if (this.selectedStudent) {
              this.loadStudentSummary(this.selectedStudent._id);
            }
          },
          error: (err) => {
            this.toast.showToast(
              err?.error?.message ?? 'Failed to assign course enrollment.',
              'error',
            );
          },
        });
    }
  }

  revokeCourseEnrollment(enrollment: any): void {
    if (!enrollment?.course?._id || !this.selectedStudent?._id) return;
    const courseId = enrollment.course._id;
    const courseTitle = enrollment.course.title || 'course';

    this.courseService
      .unenrollStudent(courseId, this.selectedStudent._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast(
            `Access to "${courseTitle}" revoked.`,
            'success',
          );
          if (this.selectedStudent) {
            this.loadStudentSummary(this.selectedStudent._id);
          }
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to revoke course access.',
            'error',
          );
        },
      });
  }

  isEnrollmentExpired(enrollment: any): boolean {
    return Boolean(
      enrollment?.expiresOn && new Date(enrollment.expiresOn) < new Date(),
    );
  }

  toggleBlockStudent(student: UserCompatible): void {
    const isBlocked = student.status === UserStatus.BLOCKED;
    const action$ = isBlocked
      ? this.userService.unblockUser(student._id)
      : this.userService.blockUser(student._id);

    this.processingAction.set(true);
    action$
      .pipe(
        finalize(() => this.processingAction.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.toast.showToast(
            isBlocked
              ? `Student ${this.getStudentName(student)} has been unblocked!`
              : `Student ${this.getStudentName(student)} has been blocked.`,
            'success',
          );
          this.loadStudents();
          if (
            this.selectedStudent &&
            this.selectedStudent._id === student._id
          ) {
            this.selectedStudent.status = isBlocked
              ? UserStatus.VERIFIED
              : UserStatus.BLOCKED;
          }
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to update student status.',
            'error',
          );
        },
      });
  }

  resendInvite(invitation: InvitationItem): void {
    this.processingAction.set(true);
    this.invitationService
      .resendInvitation(invitation._id)
      .pipe(
        finalize(() => this.processingAction.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.toast.showToast(
            `Invitation reminder resent to ${invitation.recipient}!`,
            'success',
          );
          this.loadInvitations();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to resend invitation.',
            'error',
          );
        },
      });
  }

  cancelInvite(invitation: InvitationItem): void {
    this.processingAction.set(true);
    this.invitationService
      .cancelInvitation(invitation._id)
      .pipe(
        finalize(() => this.processingAction.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.toast.showToast('Invitation cancelled successfully.', 'success');
          this.loadInvitations();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to cancel invitation.',
            'error',
          );
        },
      });
  }

  getStudentName(student: UserCompatible): string {
    const p = student.profile?.personal;
    if (p?.firstName) {
      return `${p.firstName} ${p.lastName ?? ''}`.trim();
    }
    return student.username;
  }

  getStudentInitial(student: UserCompatible): string {
    const name = this.getStudentName(student);
    return name.charAt(0).toUpperCase() || 'S';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'VERIFIED':
      case 'ACTIVE':
      case 'ACCEPTED':
        return 'daisy-badge-success';
      case 'BLOCKED':
      case 'CANCELLED':
        return 'daisy-badge-error';
      case 'EXPIRED':
        return 'daisy-badge-neutral';
      default:
        return 'daisy-badge-warning';
    }
  }

  getStudentEmail(student: UserCompatible | null): string | null {
    if (!student) return null;
    const summary =
      this.selectedStudent?._id === student._id ? this.studentSummary : null;
    const email =
      summary?.profile?.personal?.email ||
      student.profile?.personal?.email ||
      student.email ||
      student.username;
    return email && isEmail(email.trim()) ? email.trim() : null;
  }

  getStudentMobile(student: UserCompatible | null): string | null {
    if (!student) return null;
    const summary =
      this.selectedStudent?._id === student._id ? this.studentSummary : null;
    const mobile =
      summary?.profile?.personal?.mobile ||
      student.profile?.personal?.mobile ||
      student.mobile ||
      (!isEmail(student.username) ? student.username : null);
    return mobile?.trim() || null;
  }

  // --- Manual Student Onboarding ---
  openAddManualStudentModal(): void {
    this.manualStudentForm = { ...INITIAL_MANUAL_STUDENT_FORM };
    this.showAddManualStudentModal = true;
    if (this.availableClasses.length === 0 && this.availableCourses.length === 0) {
      this.loadLearningItems();
    }
  }

  closeAddManualStudentModal(): void {
    this.showAddManualStudentModal = false;
  }

  submitAddManualStudent(): void {
    const firstName = this.manualStudentForm.firstName?.trim();
    if (!firstName) {
      this.toast.showToast('First name is required.', 'error');
      return;
    }

    const email = this.manualStudentForm.email?.trim();
    const mobile = this.manualStudentForm.mobile?.trim();
    if (!email && !mobile) {
      this.toast.showToast(
        'Please provide an email address or mobile number.',
        'error',
      );
      return;
    }

    this.submittingManualStudent.set(true);

    this.userService
      .addManualStudent({
        firstName,
        lastName: this.manualStudentForm.lastName?.trim() || undefined,
        email: email || undefined,
        mobile: mobile || undefined,
        studentId: this.manualStudentForm.studentId?.trim() || undefined,
        liveClassId: this.manualStudentForm.liveClassId || undefined,
        courseId: this.manualStudentForm.courseId || undefined,
        notes: this.manualStudentForm.notes?.trim() || undefined,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.submittingManualStudent.set(false)),
      )
      .subscribe({
        next: (res) => {
          this.toast.showToast(
            res.message ||
              'Student onboarded successfully and credentials dispatched.',
            'success',
          );
          this.closeAddManualStudentModal();
          this.loadStudents();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ||
              'Failed to add student. Please check input details.',
            'error',
          );
        },
      });
  }

  // --- Admin Password Reset ---
  openAdminResetPassword(student: UserCompatible): void {
    this.resetPasswordStudent = student;
    this.newPassword = '';
    this.showResetPasswordModal = true;
  }

  closeResetPasswordModal(): void {
    this.showResetPasswordModal = false;
    this.resetPasswordStudent = null;
    this.newPassword = '';
  }

  submitAdminResetPassword(): void {
    if (!this.resetPasswordStudent?._id) return;

    if (!this.newPassword || this.newPassword.length < 6) {
      this.toast.showToast(
        'Password must be at least 6 characters long.',
        'error',
      );
      return;
    }

    this.submittingResetPassword.set(true);

    this.userService
      .adminResetPassword(this.resetPasswordStudent._id, this.newPassword)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.submittingResetPassword.set(false)),
      )
      .subscribe({
        next: (res) => {
          this.toast.showToast(
            res.message || 'Student password has been reset successfully.',
            'success',
          );
          this.closeResetPasswordModal();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message || 'Failed to reset student password.',
            'error',
          );
        },
      });
  }
}
